import type { PropagationContext } from '@/store/rate-propagation.store'
import { useCallback } from 'react'
import { memberRateApi } from '@/lib/api/member-resources.api'
import { useRatePropagationStore } from '@/store/rate-propagation.store'
import { useQueryClient } from '@tanstack/react-query'

import { queryKeys } from './query-keys'

type PropagationResult = {
  updated: number
  skipped: number
  failed: {
    entryId: string
    memberName: string
    projectName: string
    reason: string
    date: string
  }[]
}

/**
 * Propagate one or more rate changes and fold the results into one report.
 * Each context is a separate request (the API scopes a run to one member,
 * rate card, project, entity or role); a failed request becomes one failed
 * row carrying `failureReason`, as a single failed run always has.
 */
export async function propagateRateContexts(
  contexts: PropagationContext[],
  failureReason = 'Propagation request failed',
): Promise<PropagationResult & { anySucceeded: boolean }> {
  const settled = await Promise.allSettled(
    contexts.map(context => memberRateApi.propagateRateChanges(context)),
  )
  const combined: PropagationResult & { anySucceeded: boolean } = {
    updated: 0,
    skipped: 0,
    failed: [],
    anySucceeded: false,
  }
  for (const outcome of settled) {
    if (outcome.status === 'rejected') {
      combined.failed.push({
        entryId: '',
        memberName: '',
        projectName: '',
        reason: failureReason,
        date: '',
      })
      continue
    }
    combined.anySucceeded = true
    combined.updated += outcome.value.updated
    combined.skipped += outcome.value.skipped
    combined.failed.push(
      ...outcome.value.failed.map(f => ({
        entryId: f.entryId,
        memberName: f.memberName || '---',
        projectName: f.projectName || '---',
        reason: f.reason,
        date: f.date || '---',
      })),
    )
  }
  return combined
}

/**
 * Hook to trigger rate propagation and drive the RatePropagationNotifier.
 * Call `triggerPropagation()` from any mutation's onSuccess callback
 * when the mutation affects rates (rate CRUD, compensation, role assignment, rate cards).
 *
 * Use `triggerPropagationWithImpactCheck()` instead when the action is user-initiated
 * (e.g. saving a rate in the team module). It calls check-impact first and, if DRAFT or
 * SUBMITTED entries would be affected, opens RateImpactDialog to let the user choose
 * what to update before propagation runs.
 *
 * Use `triggerBatchPropagationWithImpactCheck()` when one user action changes several
 * rates at once (bulk rate creation). The store holds one pending impact action and one
 * progress state, so the batch asks once and reports once.
 *
 * Retry is handled by the RatePropagationNotifier component (always mounted in App.tsx)
 * which watches for `retryRequested` and re-runs propagation with the saved context.
 */
export function useRatePropagation() {
  const queryClient = useQueryClient()
  const {
    startPropagation,
    setContext,
    completePropagation,
    openImpactDialog,
  } = useRatePropagationStore()

  const runPropagation = useCallback(
    async (contexts: PropagationContext[]) => {
      setContext(contexts.length === 1 ? contexts[0] : contexts)
      startPropagation()

      const result = await propagateRateContexts(contexts)
      completePropagation(result)
      if (!result.anySucceeded) return
      queryClient.invalidateQueries({ queryKey: queryKeys.timeEntries.all })
      queryClient.invalidateQueries({
        queryKey: queryKeys.memberResources.earnings(),
      })
      queryClient.invalidateQueries({
        queryKey: queryKeys.memberResources.myEarnings(),
      })
      queryClient.invalidateQueries({
        queryKey: queryKeys.memberResources.pendingEarnings(),
      })
      queryClient.invalidateQueries({
        queryKey: queryKeys.memberResources.utilization(),
      })
      queryClient.invalidateQueries({
        queryKey: queryKeys.memberResources.teamUtilization(),
      })
      queryClient.invalidateQueries({
        queryKey: queryKeys.memberResources.insights(),
      })
    },
    [setContext, startPropagation, completePropagation, queryClient],
  )

  const triggerPropagation = useCallback(
    (context: PropagationContext) => runPropagation([context]),
    [runPropagation],
  )

  /**
   * Check if a rate change affects any DRAFT or SUBMITTED entries before propagating.
   * If affected entries exist, opens RateImpactDialog to let the user choose what to update.
   * If none exist, falls through directly to full propagation (existing behavior).
   */
  const triggerPropagationWithImpactCheck = useCallback(
    async (context: Omit<PropagationContext, 'statusFilter'>) => {
      try {
        const impact = await memberRateApi.checkRateChangeImpact(context)
        if (impact.draftCount > 0 || impact.submittedCount > 0) {
          openImpactDialog(impact, async statusFilter => {
            await triggerPropagation({ ...context, statusFilter })
          })
          return
        }
      } catch {
        // If the impact check fails, fall through to full propagation
      }
      await triggerPropagation(context)
    },
    [triggerPropagation, openImpactDialog],
  )

  /**
   * Batch form of `triggerPropagationWithImpactCheck`: checks every context, opens
   * RateImpactDialog once with the summed counts, and on the user's answer propagates
   * all of them in one run. Contexts with affected entries get the chosen statusFilter;
   * the rest (and any whose check failed) propagate in full, exactly as the single path
   * would. Those wait for the answer too, so the batch reports one combined result.
   */
  const triggerBatchPropagationWithImpactCheck = useCallback(
    async (contexts: Omit<PropagationContext, 'statusFilter'>[]) => {
      if (contexts.length === 0) return
      const impacts = await Promise.all(
        contexts.map(context =>
          memberRateApi.checkRateChangeImpact(context).catch(() => null),
        ),
      )
      const affected: typeof contexts = []
      const unaffected: typeof contexts = []
      const counts = { draftCount: 0, submittedCount: 0 }
      contexts.forEach((context, index) => {
        const impact = impacts[index]
        if (impact && (impact.draftCount > 0 || impact.submittedCount > 0)) {
          affected.push(context)
          counts.draftCount += impact.draftCount
          counts.submittedCount += impact.submittedCount
        } else {
          unaffected.push(context)
        }
      })

      if (affected.length === 0) {
        await runPropagation(contexts)
        return
      }
      openImpactDialog(counts, async statusFilter => {
        await runPropagation([
          ...affected.map(context => ({ ...context, statusFilter })),
          ...unaffected,
        ])
      })
    },
    [runPropagation, openImpactDialog],
  )

  return {
    triggerPropagation,
    triggerPropagationWithImpactCheck,
    triggerBatchPropagationWithImpactCheck,
  }
}
